import {
	formatIstDate,
	formatIstDateTime,
	formatQueryId,
	parseTimestamp,
	type WorkStatus,
	workStatusLabel,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { FloatingAction } from "@anthra/ui/floating-action"
import { Pagination } from "@anthra/ui/pagination"
import { Skeleton } from "@anthra/ui/skeleton"
import { ToggleGroup, ToggleGroupItem } from "@anthra/ui/toggle-group"
import {
	Body,
	CardTitle,
	Description,
	Meta,
	Overline,
	PageTitle,
	SectionTitle,
} from "@anthra/ui/typography"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { InfoIcon, TriangleAlertIcon } from "lucide-react"
import { useState } from "react"

import { ItemBadge } from "@/features/activity/item-badge"
import { PersonalisedServicesBanner } from "@/features/content/personalised-services-banner"
import { useMyQueries } from "@/features/queries/use-queries"

/**
 * My Queries (US-00-002.6/.8, US-00-005.10).
 *
 * Built against screens/my-queries.html: the status chips, the query cards in
 * their four-column grid, numbered pagination and the Personalised Services
 * banner. ONLY THE SIGNED-IN USER'S OWN QUERIES, most recent first — ownership is
 * never a parameter, so nothing here could be edited to see somebody else's list
 * (US-00-006.2).
 *
 * NO SEARCH BOX. The design draws one, but `MyQueriesFilters` — and the API
 * behind it — takes a status only; there is no query-text parameter to send it
 * to. A box that filtered only the page already on screen would look like search
 * and not be it, so it is left out rather than built to lie.
 */
export const Route = createFileRoute("/_app/queries/")({
	component: MyQueries,
})

function MyQueries() {
	const navigate = useNavigate()
	const [status, setStatus] = useState<WorkStatus | undefined>(undefined)
	const [page, setPage] = useState(1)
	const queries = useMyQueries(status ? { status } : {}, page)

	// One light call per chip, purely for its count — page 1 of each status, which
	// react-query dedupes against the main list call whenever they resolve to the
	// same key (the selected chip, at page 1). Named individually rather than
	// built from a loop: a hook called from inside Array.map is exactly the shape
	// the rules-of-hooks lint exists to catch, whether or not the array itself
	// ever changes length.
	const all = useMyQueries({}, 1)
	const open = useMyQueries({ status: "open" }, 1)
	const inProgress = useMyQueries({ status: "in_progress" }, 1)
	const answered = useMyQueries({ status: "answered" }, 1)
	const closed = useMyQueries({ status: "closed" }, 1)

	const CHIPS: { status: WorkStatus | undefined; label: string; count: number | undefined }[] = [
		{ status: undefined, label: "All", count: all.data?.pagination.totalCount },
		{ status: "open", label: workStatusLabel("open"), count: open.data?.pagination.totalCount },
		{
			status: "in_progress",
			label: workStatusLabel("in_progress"),
			count: inProgress.data?.pagination.totalCount,
		},
		{
			status: "answered",
			label: workStatusLabel("answered"),
			count: answered.data?.pagination.totalCount,
		},
		{
			status: "closed",
			label: workStatusLabel("closed"),
			count: closed.data?.pagination.totalCount,
		},
	]

	function selectChip(next: WorkStatus | undefined): void {
		setStatus(next)
		setPage(1)
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-4">
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="flex min-w-0 flex-col gap-1">
						<PageTitle>My questions</PageTitle>
						<Description>Newest first. Only you can see these.</Description>
					</div>
					<Link
						className={cn(
							buttonVariants({ variant: "primary" }),
							"hidden shrink-0 lg:inline-flex",
						)}
						to="/queries/new"
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							aria-hidden="true"
						>
							<path d="M12 5v14M5 12h14" />
						</svg>
						Ask a query
					</Link>
				</div>
			</div>

			{/* One choice of five, not five independent toggles. The chips carried
			    `aria-pressed`, which describes a switch that is on or off, so a
			    screen reader announced five of them rather than one filter with a
			    current value. The row scrolls at 375px instead of wrapping and
			    pushing the list itself below the fold. */}
			<ToggleGroup
				type="single"
				value={status ?? "all"}
				onValueChange={next => {
					if (next === "") return
					selectChip(next === "all" ? undefined : (next as WorkStatus))
				}}
				aria-label="Filter by status"
			>
				{CHIPS.map(chip => (
					<ToggleGroupItem
						key={chip.label}
						value={chip.status ?? "all"}
					>
						{chip.label}
						<span className="text-meta opacity-70">{chip.count ?? "—"}</span>
					</ToggleGroupItem>
				))}
			</ToggleGroup>

			{queries.isPending ? (
				<>
					<p
						className="sr-only"
						role="status"
					>
						Loading your queries.
					</p>
					<ul
						className="flex flex-col gap-2"
						aria-hidden="true"
					>
						{["a", "b", "c", "d", "e", "f"].map(key => (
							<li key={`skel-${key}`}>
								<div className="rounded-card border-border bg-card shadow-card grid gap-2 border p-4 lg:grid-cols-[minmax(0,1fr)_10rem_8rem_7rem] lg:items-center lg:gap-3">
									<div className="flex flex-col gap-2">
										<Skeleton className="h-3 w-16" />
										<Skeleton
											className={cn(
												"h-4",
												key === "b" || key === "e" ? "w-1/2" : "w-3/4",
											)}
										/>
									</div>
									<Skeleton className="h-4 w-24" />
									<Skeleton className="h-4 w-20" />
									<Skeleton className="rounded-pill h-6 w-20" />
								</div>
							</li>
						))}
					</ul>
				</>
			) : null}

			{queries.isError && !queries.data ? (
				<div
					className="rounded-card border-border bg-card shadow-card flex flex-col items-center gap-3 border p-10 text-center"
					role="alert"
				>
					<span
						className="bg-status-breach-bg text-status-breach-fg rounded-circle flex size-14 items-center justify-center"
						aria-hidden="true"
					>
						<TriangleAlertIcon
							className="size-7 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<SectionTitle as="h3">{toDisplayable(queries.error).title}</SectionTitle>
					<Description className="max-w-prose">
						{toDisplayable(queries.error).message}
					</Description>
					<div className="flex flex-wrap justify-center gap-2">
						<button
							type="button"
							className={cn(buttonVariants({ variant: "primary" }))}
							onClick={() => {
								void queries.refetch()
							}}
						>
							Try again
						</button>
						<Link
							className={cn(buttonVariants({ variant: "secondary" }))}
							to="/home"
						>
							Go to Home
						</Link>
					</div>
				</div>
			) : null}

			{/* The list loaded once, but the refresh on this focus/navigation
			    failed — the earlier page stays on screen rather than being
			    replaced by an error takeover, with a strip that says the
			    statuses shown may be behind (screens/my-queries.html's 4c). */}
			{queries.isError && queries.data ? (
				<div
					className="rounded-card border-status-in-progress-border bg-status-in-progress-bg flex items-start gap-3 border p-4"
					role="status"
				>
					<InfoIcon
						className="size-5 shrink-0"
						aria-hidden="true"
					/>
					<div className="flex min-w-0 flex-col gap-1">
						<Body>Statuses may be out of date</Body>
						<Description>{toDisplayable(queries.error).message}</Description>
						<div className="mt-1 flex flex-wrap gap-2">
							<button
								type="button"
								className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
								onClick={() => {
									void queries.refetch()
								}}
							>
								Try again
							</button>
						</div>
					</div>
				</div>
			) : null}

			{queries.data && queries.data.data.length === 0 ? (
				<div className="rounded-card border-border bg-card shadow-card flex flex-col items-center gap-3 border p-10 text-center">
					<span
						className="bg-subtle text-primary rounded-circle flex size-14 items-center justify-center"
						aria-hidden="true"
					>
						<svg
							width="28"
							height="28"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M6 3h8l4 4.5V21H6z" />
							<path d="M14 3v4.5h4" />
							<path d="M10 12.2a2 2 0 1 1 2.6 1.9c-.5.2-.8.7-.8 1.2v.4" />
							<path d="M11.8 18h.01" />
						</svg>
					</span>
					<SectionTitle as="h3">
						{status === undefined
							? "No questions yet"
							: `No ${workStatusLabel(status)} questions`}
					</SectionTitle>
					<Description className="max-w-prose">
						{status === undefined
							? "Send us a notice and a short description. Your question appears here with its status."
							: "Try another status, or look across all of them."}
					</Description>
					<div className="flex flex-wrap justify-center gap-2">
						<Link
							className={cn(buttonVariants({ variant: "primary" }))}
							to="/queries/new"
						>
							Ask an expert
						</Link>
						<Link
							className={cn(buttonVariants({ variant: "secondary" }))}
							to="/vetting/new"
						>
							Get a draft vetted instead
						</Link>
					</div>
				</div>
			) : null}

			{queries.data && queries.data.data.length > 0 ? (
				<>
					{/* The column headers, and only where there are columns. Below lg
					    each card stacks, so a header row would label nothing. They
					    take the overline role — 11px, tracked, uppercase, muted — so
					    a header reads as a header rather than as another line of
					    card text. */}
					<div
						className="hidden gap-3 border border-transparent px-4 lg:grid lg:grid-cols-[minmax(0,1fr)_8rem_7rem]"
						aria-hidden="true"
					>
						<Overline as="span">Question</Overline>
						<Overline as="span">Submitted</Overline>
						<Overline as="span">Status</Overline>
					</div>

					<ul className="flex flex-col gap-2">
						{queries.data.data.map(query => {
							const submitted = parseTimestamp(query.submittedAt)
							const lastUpdated = parseTimestamp(query.lastUpdatedAt)
							return (
								<li key={query.id}>
									<Link
										className="rounded-card border-border bg-card shadow-card hover:border-border-strong hover:bg-surface-hover focus-visible:ring-ring flex flex-col gap-2 border p-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
										to="/queries/$queryId"
										params={{ queryId: String(query.sequence) }}
									>
										{/* One column on a phone, four from lg up, matching the
										    header above. The four-column grid applied at every
										    width, so at 375px the category, date and status each
										    got a quarter of the line and the title got the rest
										    of nothing. */}
										<div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_8rem_7rem] lg:items-center lg:gap-3">
											<div className="flex min-w-0 flex-col gap-1">
												<Meta>{formatQueryId(query.sequence)}</Meta>
												<span className="flex min-w-0 items-center">
													<CardTitle
														className={cn(
															"max-w-full truncate",
															query.hasUnseen && "font-bold",
														)}
													>
														{query.title}
													</CardTitle>
													<ItemBadge count={query.unseenCount} />
												</span>
												<Meta>
													{query.status === "closed"
														? "Closed "
														: "Updated "}
													{lastUpdated
														? formatIstDateTime(lastUpdated)
														: "—"}
												</Meta>
											</div>
											<Description>
												{submitted ? formatIstDate(submitted) : "—"}
											</Description>
											<Description className="capitalize">
												{query.status}
											</Description>
										</div>
										{query.status === "answered" ? (
											<p className="text-meta text-primary flex items-center gap-1.5 font-medium">
												<svg
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
													aria-hidden="true"
												>
													<path d="m11.3 3.5 2.1 4.3 4.7.7-3.4 3.3.8 4.7-4.2-2.2-4.2 2.2.8-4.7-3.4-3.3 4.7-.7z" />
												</svg>
												Rate this answer
											</p>
										) : null}
									</Link>
								</li>
							)
						})}
					</ul>

					<Pagination
						pagination={queries.data.pagination}
						page={page}
						onPageChange={setPage}
					/>
				</>
			) : null}

			{/* Shown on this screen whether or not there are any queries
			    (US-00-005.10). */}
			<PersonalisedServicesBanner screen="my_queries" />
			{/* The primary create, where a thumb is. The head button above is
			    hidden below lg, so there is one primary action per breakpoint
			    rather than two competing ones. */}
			<FloatingAction
				label="Ask a query"
				onClick={() => {
					void navigate({ to: "/queries/new" })
				}}
			/>
		</div>
	)
}
